/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NotificationService } from '@alfresco/adf-core';
import { ContentNodeSelectorComponent, ContentNodeSelectorComponentData, NodeAction } from '@alfresco/adf-content-services';
import { Node } from '@alfresco/js-api';
import { ContentApiService, PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DmsMetadataService } from '../upload/dms-metadata.service';
import { FavoriteCounts, FavoriteKind, FavoriteRow, FavoriteSort, FavoritesService } from './favorites.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const TABS: { kind: FavoriteKind; label: string }[] = [
  { kind: 'all', label: 'DMS.UI.BD5D57' },
  { kind: 'file', label: 'DMS.UI.D997F7' },
  { kind: 'folder', label: 'DMS.UI.848311' },
  { kind: 'site', label: 'DMS.UI.B91105' }
];

const SORTS: { value: FavoriteSort; label: string }[] = [
  { value: 'recent', label: 'DMS.UI.439DBB' },
  { value: 'modified', label: 'DMS.UI.427153' },
  { value: 'name', label: 'DMS.UI.F882A4' }
];

const BADGES: Record<string, string> = {
  pdf: 'PDF',
  doc: 'W',
  docx: 'W',
  xls: 'X',
  xlsx: 'X',
  ppt: 'P',
  pptx: 'P',
  txt: 'TXT',
  jpg: 'IMG',
  jpeg: 'IMG',
  png: 'IMG'
};

const PAGE_SIZE = 100;

/**
 * Favourites (UI-02).
 *
 * Tabs and the list are Alfresco's favourites API; the pinned strip along the
 * top is a server-side user preference — see {@link FavoritesService}.
 *
 * The list is fetched a page at a time and ordered in the browser: the
 * favourites API returns the whole entry, but does not sort by the fields this
 * screen offers. With more than one page of favourites the sort therefore
 * applies to the loaded page, which {@link hasMore} makes visible rather than
 * pretending the ordering is global.
 */
@Component({
  selector: 'app-favorites-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './favorites-page.component.html',
  styleUrls: ['./favorites-page.component.scss']
})
export class FavoritesPageComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly favorites = inject(FavoritesService);
  private readonly content = inject(ContentApiService);
  private readonly notification = inject(NotificationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  readonly tabs = TABS;
  readonly sorts = SORTS;

  readonly rows = signal<FavoriteRow[]>([]);
  readonly counts = signal<FavoriteCounts | null>(null);
  readonly loading = signal(true);
  readonly failed = signal('');
  readonly hasMore = signal(false);
  readonly view = signal<'list' | 'grid'>('list');
  readonly pinnedOnly = signal(false);

  kind: FavoriteKind = 'all';
  sort: FavoriteSort = 'recent';

  /** Pinned favourites, shown as the card strip at the top. */
  readonly pinnedRows = computed(() => this.rows().filter((row) => row.pinned));

  /** Rows for the table, honouring the sort and the pinned-only toggle. */
  readonly listRows = computed(() => {
    const rows = this.pinnedOnly() ? this.pinnedRows() : this.rows();
    const sorted = [...rows];

    switch (this.sortValue()) {
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name, 'th'));
      case 'modified':
        return sorted.sort((a, b) => (b.modifiedAt || '').localeCompare(a.modifiedAt || ''));
      default:
        return sorted.sort((a, b) => (b.favoritedAt || '').localeCompare(a.favoritedAt || ''));
    }
  });

  /** Mirrors `sort` into the signal graph so `listRows` recomputes on change. */
  private readonly sortValue = signal<FavoriteSort>('recent');

  ngOnInit(): void {
    this.reload();
  }

  // ---- data ---------------------------------------------------------------

  reload(): void {
    this.loading.set(true);
    this.failed.set('');

    this.favorites.list(this.kind, 0, PAGE_SIZE).subscribe({
      next: ({ rows, total }) => {
        this.rows.set(rows);
        this.hasMore.set(total > rows.length);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.rows.set([]);
        this.failed.set(this.describe(err, 'DMS.UI.867798'));
      }
    });

    this.favorites.counts().subscribe({
      next: (counts) => this.counts.set(counts),
      error: () => this.counts.set(null)
    });
  }

  selectTab(kind: FavoriteKind): void {
    if (this.kind === kind) {
      return;
    }
    this.kind = kind;
    this.reload();
  }

  changeSort(sort: FavoriteSort): void {
    this.sort = sort;
    this.sortValue.set(sort);
  }

  togglePinnedOnly(): void {
    this.pinnedOnly.update((on) => !on);
  }

  setView(view: 'list' | 'grid'): void {
    this.view.set(view);
  }

  countFor(kind: FavoriteKind): number | null {
    const counts = this.counts();
    return counts ? counts[kind] : null;
  }

  // ---- actions ------------------------------------------------------------

  /** Pins or unpins, then reflects the server's new set back into the rows. */
  togglePin(row: FavoriteRow): void {
    this.favorites.setPinned(row.id, !row.pinned).subscribe({
      next: (pinned) => this.rows.update((rows) => rows.map((r) => ({ ...r, pinned: pinned.has(r.id) }))),
      error: (err) => this.notification.showError(this.describe(err, 'DMS.UI.EA89B5'))
    });
  }

  removeFavorite(row: FavoriteRow): void {
    this.favorites.remove(row.id).subscribe({
      next: () => {
        this.rows.update((rows) => rows.filter((r) => r.id !== row.id));
        this.favorites.counts().subscribe((counts) => this.counts.set(counts));
        this.notification.showInfo(this.translate.instant('DMS.UI.FB94A2', { p0: row.name }));
      },
      error: (err) => this.notification.showError(this.describe(err, 'DMS.UI.104BE7'))
    });
  }

  open(row: FavoriteRow): void {
    void this.router.navigate(row.kind === 'file' ? ['/document', row.id] : ['/personal-files', row.id]);
  }

  download(row: FavoriteRow): void {
    if (row.kind === 'file') {
      window.open(this.content.getContentUrl(row.id, true), '_blank');
    }
  }

  /** Adds favourites by picking nodes, so the screen is not a dead end when empty. */
  addFavorites(): void {
    const select = new Subject<Node[]>();
    const data: ContentNodeSelectorComponentData = {
      selectionMode: 'multiple',
      title: 'DMS.UI.7A6798',
      actionName: NodeAction.CHOOSE,
      // favourites can live anywhere, so browsing starts at the repository root
      currentFolderId: '-root-',
      select
    };

    this.dialog.open(ContentNodeSelectorComponent, { data, panelClass: 'adf-content-node-selector-dialog', width: '630px' });

    select.subscribe((selections: Node[]) => {
      select.complete();
      const nodes = selections ?? [];
      if (!nodes.length) {
        return;
      }

      forkJoin(nodes.map((node) => this.favorites.add(node.id, !!node.isFolder).pipe(catchError(() => of(null))))).subscribe((results) => {
        const added = results.filter(Boolean).length;
        const failed = results.length - added;
        if (added) {
          this.notification.showInfo(this.translate.instant('DMS.UI.37B1BF', { p0: added }));
        }
        if (failed) {
          this.notification.showWarning(this.translate.instant('DMS.UI.8F887C', { p0: failed }));
        }
        this.reload();
      });
    });
  }

  // ---- view helpers -------------------------------------------------------

  label(value: string): string {
    return value ? DmsMetadataService.label(value) : '';
  }

  badge(row: FavoriteRow): string {
    return row.kind === 'file' ? (BADGES[this.extension(row.name)] ?? 'FILE') : '';
  }

  badgeClass(row: FavoriteRow): string {
    return 'aca-fav__badge--' + (row.kind === 'file' ? this.extension(row.name) : row.kind);
  }

  kindLabel(row: FavoriteRow): string {
    return row.kind === 'file' ? 'DMS.UI.D997F7' : row.kind === 'folder' ? 'DMS.UI.848311' : 'DMS.UI.B91105';
  }

  statusClass(status: string): string {
    return 'aca-fav__chip--' + (status || 'none');
  }

  initials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || (name?.[0] ?? '?').toUpperCase();
  }

  trackById(_: number, row: FavoriteRow): string {
    return row.id;
  }

  private extension(name: string): string {
    return name.split('.').pop()?.toLowerCase() ?? '';
  }

  private describe(error: any, fallback: string): string {
    return error?.error?.error?.briefSummary || error?.message || this.translate.instant(fallback);
  }
}
